// import Icon from '@ant-design/icons'; // Заменено на прямое использование SVG

import {useTranslations} from 'next-intl';
import {type FC, Fragment, memo, type ReactElement, useMemo} from 'react';
import ChevronRight from '../../../../public/icons/chevron-right.svg';
import ChevronRightDouble from '../../../../public/icons/chevron-right-double.svg';
import useMobile from '../../../hooks/useMobile';

interface PaginationControlProps {
  page: number;
  totalPages: number;
  className?: string;

  firstPage(): void;

  lastPage(): void;

  prevPage(): void;

  nextPage(): void;

  setPage(page: number): void;
}

export const PaginationControl: FC<PaginationControlProps> = memo(
  ({page, totalPages, firstPage, lastPage, prevPage, nextPage, setPage, className = ''}): ReactElement | null => {
    const t = useTranslations('NFT.Pagination');
    const mobile = useMobile();

    const pages = useMemo<Array<number>>(() => {
      const uniq = new Set<number>();

      uniq.add(0);

      if (totalPages) uniq.add(totalPages - 1);

      const dx = mobile ? 1 : 3;

      for (let i = page - dx; i <= page + dx; i++) {
        if (i >= 0 && i <= totalPages - 1) {
          uniq.add(i);
        }
      }

      return Array.from(uniq).sort((a, b) => (a > b ? 1 : -1));
    }, [page, totalPages, mobile]);

    return (
      <div className={`flex items-center justify-center lg:justify-between ${className}`}>
        <button
          type="button"
          className="hidden items-center gap-x-4 fill-transparent p-3.5 text-sm font-semibold uppercase leading-[1] text-[#3d63e8] disabled:cursor-not-allowed disabled:opacity-60 lg:inline-flex"
          onClick={firstPage}
          disabled={!page}
        >
          <ChevronRightDouble className="text-base" style={{transform: 'rotate(180deg)'}} />
          <span className="hidden lg:inline-block">{t('First')}</span>
        </button>
        <div className="inline-flex items-center justify-center gap-4">
          <button
            type="button"
            className="inline-flex items-center fill-transparent uppercase text-[#3d63e8] disabled:cursor-not-allowed disabled:opacity-60"
            onClick={prevPage}
            disabled={!page}
          >
            <ChevronRight className="text-base" style={{transform: 'rotate(180deg)'}} />
          </button>

          <div className="inline-flex items-center">
            {pages.map((p, idx, arr) => {
              const prev = arr[idx - 1] ?? 0;
              const dx = p - prev;
              const active = p === page;

              return (
                <Fragment key={p}>
                  {dx > 1 && (
                    <span className="p-3 text-sm font-semibold leading-[1] text-[#F0F0F0] md:min-w-11">…</span>
                  )}
                  <button
                    type="button"
                    className={`p-3 text-sm font-semibold leading-[1] md:min-w-11 ${
                      active ? 'text-[#F0F0F0]' : 'text-[#3d63e8]'
                    } font-semibold`}
                    onClick={() => setPage(p)}
                  >
                    {p + 1}
                  </button>
                </Fragment>
              );
            })}
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center fill-transparent uppercase text-[#3D63E8] disabled:cursor-not-allowed disabled:opacity-30"
            onClick={nextPage}
            disabled={page >= totalPages - 1}
          >
            <ChevronRight className="text-base" />
          </button>
        </div>
        <button
          type="button"
          className="hidden items-center gap-x-4 fill-transparent p-3.5 text-sm font-semibold uppercase leading-[1] text-[#3d63e8] disabled:cursor-not-allowed disabled:opacity-60 lg:inline-flex"
          onClick={lastPage}
          disabled={page >= totalPages - 1}
        >
          <span className="hidden lg:inline-block">{t('Last')}</span>
          <ChevronRightDouble className="text-base" />
        </button>
      </div>
    );
  },
);

PaginationControl.displayName = 'PaginationControl';
